'use client';

/**
 * Receipt for a server-side code execution: exit status, then whatever the
 * sandbox actually printed. The code Claude ran is already shown by the tool
 * input renderer, so this card deliberately shows only the OUTPUT.
 */

import React from 'react';
import { truncateStream, type CodeExecutionView } from './code-execution-view';

export function CodeExecutionResult({ view }: { view: CodeExecutionView }) {
  const failed = view.errorCode !== null || (view.returnCode !== null && view.returnCode !== 0);
  const stdout = view.stdout ? truncateStream(view.stdout) : null;
  const stderr = view.stderr ? truncateStream(view.stderr) : null;

  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
      <div className="flex items-center gap-2">
        <span className={failed ? 'font-medium text-destructive' : 'font-medium text-primary'}>
          {failed ? 'Failed' : 'Completed'}
        </span>
        {view.errorCode && <span className="text-muted-foreground">{view.errorCode}</span>}
        {view.errorCode === null && view.returnCode !== null && (
          <span className="text-muted-foreground">exit {view.returnCode}</span>
        )}
        {view.fileCount > 0 && (
          <span className="ml-auto text-muted-foreground">
            {view.fileCount} file{view.fileCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {stdout && stdout.text.trim().length > 0 && (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed">
          {stdout.text}
          {stdout.truncated && <span className="text-muted-foreground">… truncated</span>}
        </pre>
      )}

      {stderr && stderr.text.trim().length > 0 && (
        <pre className="text-destructive max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed">
          {stderr.text}
          {stderr.truncated && <span className="text-muted-foreground">… truncated</span>}
        </pre>
      )}
    </div>
  );
}
